import {Injectable} from "@angular/core";
import {StoreSelectorsService} from "./store-selectors.service";
import {actionGetLocalStorageConfigServer, actionGetLocalStorageConfigServerReply, actionPostChatArchiveReply, actionPreloginResellerRequest, actionSetLocalStorageConfig, localStorageUpdates} from "../store/actions/user.actions";
import {BehaviorSubject} from "rxjs";
import {EnumStudioType, ExperienceLevelType, ICartCreate, ISharedResourceLibrary, StringBinary} from "../interfaces";
import {DEFAULT_TEMPLATE_ID, isNullOrUndefOrEmpty} from "../common";
import {Lib} from "../../libs/Lib";
import * as _ from 'lodash';
import {once} from "../decorators/once-decorator";
import {ofType} from "@ngrx/effects";
import {ActionsSubject} from "@ngrx/store";

const {version: version} = require('../../../package.json');
const {localStoreConfigVersion: localStoreConfigVersion} = require('../../../package.json');
const {localStoreSharedLibraryVersion: localStoreSharedLibraryVersion} = require('../../../package.json');

export type ILocalStoreTypes =
    'ICartCreate'
    | 'IUserWork'
    | 'ILocalStorageConfig'
    | 'IPreloginReseller'
    | 'ISharedResourceLibrary'
    | 'IUser'
    | 'CLEAR'
    | 'FASTERQ';

export interface IPreloginReseller {
    resellerId: string;
}

interface IConfigItem {
    key: string;
    val: any;
}

export interface ILocalStorageConfig {
    version: string;
    studioId: string;



    noLoginUserName: string;
    sceneFilter: string;
    painterFilter: string;
    sceneCategory: string;
    painterCategory: string;
    stationFilter: string;
    dropbox_key: string;
    localStoreConfigVersion: string;
    defaultLanguage: string;
    sceneOpacityLevel: string;
    snapScale: string;
    sceneCheckeredBackground: string;
    sceneGuidelinesOpacityLevel: string;
    targetPlayer: string;
    fasterqEnabled: string;
    showHelpEnabled: string;
    advancedScheduler: string;
    eventsEnabled: string;
    catalogEnabled: string;
    transitionsEnabled: string;
    musicEnabled: string;
    dashboardEnabled: string;
    scheduledEnabled: string;
    resurcesEnabled: string;
    paintEnabled: string;
    theme: string;
    useAvatar: string;
    billingAsShipping: StringBinary;
    largeLogo: string;
    showExperienceLevel: string;
    experienceLevel: ExperienceLevelType;
    watchedVideoLinks: object;
    socialUserPendingStudioType: EnumStudioType;
    socialUserPendingTemplateId: string;
    pwaHideInstaller: string;
    privateServer: string;
    onSaleShown: string;
    ldapServer: string;
    saveSceneTimer: string;
    saveSceneManual: string;
    showDirections: string;
    stickersAsEmbedded: string;
    scriptingMode: string;
    saveFullFeature: string;
    restartPlayer: string;
    restartPlayerOnSceneAssignment: string;
    accountInitialCopyResources: string;
    enableDashboard: string;
    guidelines: string;
    favoriteFonts: string;
    isWhitelabel: string;
    isYouTubeLicense: string;
    pageSize: string;

    // custom size of new scene wizard
    dimensionIndex: string;
    customSizeX: string;
    customSizeY: string;

    // config of preview scene mode
    previewMode: string;
    previewPercent: string;
    previewSizeX: string;
    previewSizeY: string;
    previewSceneId: string;
    rememberPreviewSettings: string;

    // scene editor zoom
    zoom: string;
    timelineZoom: string;
    playlistScenePreviewEnabled: string;
    recentScenePreviewEnabled: string;

    // saveSceneOnExit: string;
    playersFilter: string;
    playersConnected: string;
    playersStatsChartEnabled: string;
    templateCategories: string;

    // resource ai data
    resourceAICallDate: string;
    resourceAICalls: string;
    stationMonitorUrl: string;

    // police
    equipmentPwa: string;
    equipmentUser: string;
    equipmentEmail: string;
    equipmentId: string;
    equipmentSlider: string;
    equipmentAccess: string;

    assistant: any;
}

@Injectable()
export class LocalStoreService {

    theme = new BehaviorSubject(null);

    constructor(private storeSelector: StoreSelectorsService, private actionsSubj: ActionsSubject) {
        const storage = this.getStorageConfig();
        if (storage && storage.localStoreConfigVersion !== localStoreConfigVersion) {
            this.removeConfigStorage();
        }
        this.setDefaultLocalConfigStorage();
        this.listenLocalStorageConfigSync();
    }

    listenLocalStorageConfigSync() {
        this.actionsSubj.pipe(ofType(actionGetLocalStorageConfigServerReply))
            .subscribe((reply) => {
                const localStorageConfigRemote: ILocalStorageConfig = reply.localStoreConfig;
                const studioIdLocal = this.getStorageValConfig('studioId');
                const studioIdRemote = localStorageConfigRemote?.studioId;
                if (isNullOrUndefOrEmpty(studioIdRemote)) return;

                /* if local studioId === remote studioId, no reason to sync as user never logged out of studio */
                // if (studioIdLocal === studioIdRemote) return;
                Object.keys(localStorageConfigRemote).forEach((k) => {
                    let config = k === 'localStoreConfigVersion' ? localStoreConfigVersion : localStorageConfigRemote[k];
                    if (k === 'studioId') {
                        config = studioIdLocal || Lib.RandomString();
                    }
                    this.setStorageValConfig(k, config, false);
                });
                console.log('sync localstorage to remote config');
                this.setRemoteLocalStorageConfig();
            });
    }

    dispatchLocalUpdated(event: ILocalStoreTypes) {
        this.storeSelector.appStore.dispatch(localStorageUpdates({
            localStorageUpdates: {
                latestEvent: event,
                versionUpdate: Number(_.uniqueId())
            }
        }));
    }

    getItem(key: ILocalStoreTypes, defaultValue?: any, expiry?: boolean): any {
        const item = localStorage.getItem(key);
        if (!item) return defaultValue;
        const data: any = JSON.parse(item);
        if (expiry) {
            const now = new Date();
            if (data?.expiry && now.getTime() > data?.expiry) {
                /** if the item is expired delete the item from storage and return defaultValue or null **/
                this.removeItem(key);
                return defaultValue ? defaultValue : null;
            } else {
                return data;
            }
        } else {
            return data;
        }
    }

    setItem(key: ILocalStoreTypes, value, ttl_in_minutes?: number): void {
        if (ttl_in_minutes) {
            const ttl_in_milliseconds = ttl_in_minutes * 1000 * 60;
            const now = new Date();
            value['expiry'] = now.getTime() + ttl_in_milliseconds;
        }
        localStorage.setItem(key, JSON.stringify(value));
        this.dispatchLocalUpdated(key);
    }

    setRemoteLocalStorageConfig() {
        const val: string = window.localStorage.getItem('ILocalStorageConfig');
        this.storeSelector.dispatch(actionSetLocalStorageConfig({localStoreConfig: val}));
    }

    setDefaultLocalConfigStorage(): void {
        const val = window.localStorage.getItem('ILocalStorageConfig');
        const storage = JSON.parse(val);
        if (_.isNull(storage)) {
            const v: ILocalStorageConfig = {
                studioId: Lib.RandomString(),
                noLoginUserName: '',
                sceneFilter: '',
                painterFilter: '',
                sceneCategory: '',
                painterCategory: '',
                stationFilter: '',
                dropbox_key: '',
                version,
                defaultLanguage: 'en',
                sceneOpacityLevel: '80',
                snapScale: '0.2',
                sceneGuidelinesOpacityLevel: '90',
                sceneCheckeredBackground: '1',
                fasterqEnabled: '0',
                targetPlayer: '0',
                showHelpEnabled: '1',
                advancedScheduler: '0',
                catalogEnabled: '1',
                eventsEnabled: '1',
                transitionsEnabled: '0',
                billingAsShipping: '1',
                musicEnabled: '0',
                dashboardEnabled: '0',
                resurcesEnabled: '0',
                scheduledEnabled: '1',
                paintEnabled: '1',
                localStoreConfigVersion,
                useAvatar: '0',
                theme: '0',
                largeLogo: '1',
                watchedVideoLinks: {},
                showExperienceLevel: '1',
                experienceLevel: ExperienceLevelType.BEGINNER,
                socialUserPendingStudioType: EnumStudioType.SIGN_STUDIO,
                socialUserPendingTemplateId: DEFAULT_TEMPLATE_ID,
                pwaHideInstaller: '0',
                onSaleShown: '',
                ldapServer: '',
                privateServer: '',
                saveSceneManual: '1',
                showDirections: '1',
                stickersAsEmbedded: '1',
                saveSceneTimer: '',
                isYouTubeLicense: '0',
                scriptingMode: '1',
                saveFullFeature: '0',
                restartPlayer: '0',
                restartPlayerOnSceneAssignment: '1',
                accountInitialCopyResources: '0', // | 0 = never seen any resources | -1 = not a fresh account so no need to check |  0 > still downloading
                enableDashboard: '1',
                favoriteFonts: '',
                guidelines: '',
                pageSize: '',
                isWhitelabel: window.localStorage.getItem('IPreloginReseller') ?? '', // possibly holds a reseller id of the main enterprise master account if was passed for login?ent=12345
                dimensionIndex: '0',
                customSizeX: '',
                customSizeY: '',
                previewMode: 'size',
                previewPercent: '100',
                previewSizeX: '',
                previewSizeY: '',
                previewSceneId: '',
                rememberPreviewSettings: '0',
                zoom: '0.5',
                timelineZoom: '0.05',
                playlistScenePreviewEnabled: '1',
                recentScenePreviewEnabled: '1',
                playersFilter: '0',
                playersConnected: '0',
                playersStatsChartEnabled: '0',
                templateCategories: '',
                // saveSceneOnExit: '1',
                resourceAICallDate: '',
                resourceAICalls: '0',
                stationMonitorUrl: '',
                equipmentPwa: '',
                equipmentUser: '',
                equipmentEmail: '',
                equipmentSlider: '',
                equipmentAccess: '',
                equipmentId: '',
                assistant: { name: 'openai', autoHide: true, showThinking: true }
            };
            this.setStorageConfig(v);
        } else {
            const newStorage = JSON.parse(JSON.stringify(storage));
            if (newStorage.showExperienceLevel == null) {
                newStorage.showExperienceLevel = '0';
                newStorage.experienceLevel = ExperienceLevelType.ADVANCED;
            }
            if (newStorage.watchedVideoLinks == null) {
                newStorage.watchedVideoLinks = {};
            }

            if (newStorage.assistant == null) {
                newStorage.assistant = {};
            }
            if (newStorage.assistant.name == null) {
                newStorage.assistant.name = 'openai';
            }
            if (newStorage.assistant.autoHide == null) {
                newStorage.assistant.autoHide = true;
            }

            if (newStorage.assistant.showThinking == null) {
                newStorage.assistant.showThinking = true;
            }

            if (JSON.stringify(storage) !== JSON.stringify(newStorage)) {
                this.setStorageConfig(newStorage);
            }
        }
    }

    setStorageConfig(val: ILocalStorageConfig): void {
        window.localStorage.setItem('ILocalStorageConfig', JSON.stringify(val));
        this.dispatchLocalUpdated('ILocalStorageConfig');
    }

    setPreloginReseller(isWhitelabel) {
        if (isNullOrUndefOrEmpty(isWhitelabel)) return;
        this.setPreloginResellerRequest(isWhitelabel);
    }

    @once
    setPreloginResellerRequest(isWhitelabel) {
        window.localStorage.setItem('IPreloginReseller', JSON.stringify(isWhitelabel));
        this.setStorageValConfig('isWhitelabel', isWhitelabel);
        this.storeSelector.dispatch(actionPreloginResellerRequest({isWhitelabel}));
    }

    setStorageSharedLibrary(i_sharedResourceLibrary: ISharedResourceLibrary): void {
        try {
            const newSharedResourceLibrary: ISharedResourceLibrary = {version: null, library: null};
            newSharedResourceLibrary.version = localStoreSharedLibraryVersion;
            newSharedResourceLibrary.library = i_sharedResourceLibrary.library;
            window.localStorage.setItem('ILocalStorageSharedLibrary', JSON.stringify(newSharedResourceLibrary));
        } catch (e) {
            alert('problem accessing local storage, try clear hard disk space...');
        }

    }

    setStorageValConfig(key: string, val: any, sync = true): void {
        this.setDefaultLocalConfigStorage();
        const storage: ILocalStorageConfig = this.getStorageConfig();
        storage[key] = val;
        this.setStorageConfig(storage);
        if (key === 'theme') {
            this.changeTheme(val);
        }
        if (sync) {
            this.setRemoteLocalStorageConfig();    
        }
    }

    setStorageValConfigGroup(values: Array<IConfigItem>): void {
        if (!values || !values.length) return;
        values.forEach((v) => {
            this.setStorageValConfig(v.key, v.val, false);
        });
        this.setRemoteLocalStorageConfig();
    }

    getStorageConfig(): ILocalStorageConfig {
        this.setDefaultLocalConfigStorage();
        const val = window.localStorage.getItem('ILocalStorageConfig');
        return JSON.parse(val);
    }

    getStorageValConfig(key): any {
        this.setDefaultLocalConfigStorage();
        const storage = this.getStorageConfig();
        return storage[key];
    }

    getSharedResourceLibrary(): ISharedResourceLibrary {
        const val = window.localStorage.getItem('ILocalStorageSharedLibrary');
        const jData = JSON.parse(val);
        if (!jData || jData?.library.length === 0 || jData?.version !== localStoreSharedLibraryVersion) return null;
        return jData;
    }

    initCartCreate(value): void {
        localStorage.setItem('ICartCreate', JSON.stringify(value));
        this.dispatchLocalUpdated('ICartCreate');
    }

    getCartCreate(): ICartCreate {
        const cart: ICartCreate = this.getItem("ICartCreate");
        if (!cart) return null;
        return cart;
    }

    removeItem(key: ILocalStoreTypes): void {
        localStorage.removeItem(key);
        this.dispatchLocalUpdated(key);
    }

    removeSettingsFromStorage() {
        window.localStorage.removeItem('ILocalStorageConfig');
    }

    removeUserStorage() {
        window.localStorage.removeItem('IUser');
        this.dispatchLocalUpdated('IUser');
    }

    removeCartStorage() {
        window.localStorage.removeItem('ICartCreate');
        this.dispatchLocalUpdated('ICartCreate');
    }

    removeConfigStorage() {
        window.localStorage.removeItem('ILocalStorageConfig');
        this.dispatchLocalUpdated('ILocalStorageConfig');
    }

    unCacheApp() {
        console.log('checking sw-cache');
        if ('caches' in window) {
            caches.keys()
                .then((keyList) => {
                    return Promise.all(keyList.map((key) => {
                        console.log('deleting cache ' + key);
                        return caches.delete(key);
                    }));
                });
        }
        if (window.navigator && navigator.serviceWorker) {
            navigator.serviceWorker.getRegistrations()
                .then((registrations) => {
                    for (const registration of registrations) {
                        console.log('unregistering service worker');
                        registration['unregister']();
                    }
                });
        }
    }

    clearStorage() {
        window.localStorage.clear();
        this.dispatchLocalUpdated('CLEAR');
    }

    changeTheme(val) {
        this.theme.next(val == 1 ? 1 : 0);
    }
}




